/**
 * filter-modal-focus.js
 *
 * Accessibility fix for the collection/search "Filter" drawer.
 * (Skies — Ski Barn accessibility issue #28)
 *
 * Ski Barn actually filters products through a third-party app widget
 * (button #pt-filter-drawer-btn / panel .pt-filter-drawer, which lives
 * inside a static .pt-drawer-container wrapper), not the theme's native
 * filter drawer (#t4s-filter-hidden) — the native one is hidden via
 * `display:none` in custom.css and unused on the live site.
 *
 * The app's outer wrapper (.pt-drawer-container, carrying a
 * "pt-filter-drawer-active" class) never changes state — the open/closed
 * signal is actually the "pt-filter-drawer-open" class on the inner
 * .pt-filter-drawer panel: absent when closed, present when open.
 *
 * Neither drawer manages keyboard focus: nothing moves focus into the
 * panel when it opens, Tab can escape into the page behind it, and focus
 * isn't returned to the trigger button when the panel closes. This file
 * patches both:
 *   - #t4s-filter-hidden (theme-native, kept in case it's ever re-enabled)
 *   - .pt-filter-drawer (the app widget actually in use)
 *
 * It only *adds* behavior — it doesn't touch the theme's minified drawer
 * script or the app's own script, so it can't break how either currently
 * opens/closes itself.
 */
(function () {
  'use strict';

  var FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  // visibility:hidden keeps an element's box, so geometry alone calls it
  // visible while the browser still refuses focus() -- check styles too.
  function isVisible(el) {
    if (!el) return false;
    if (!(el.offsetWidth || el.offsetHeight || el.getClientRects().length)) return false;
    var style = window.getComputedStyle(el);
    if (style.display === 'none') return false;
    if (style.visibility === 'hidden' || style.visibility === 'collapse') return false;
    return true;
  }

  function getFocusableElements(container) {
    var nodes = container.querySelectorAll(FOCUSABLE_SELECTOR);
    var focusable = [];
    for (var i = 0; i < nodes.length; i++) {
      if (isVisible(nodes[i])) focusable.push(nodes[i]);
    }
    return focusable;
  }

  /**
   * Some widgets use a plain <div> with a click handler as a "button"
   * (e.g. the filter app's .pt-filter-drawer-close-btn), which is
   * invisible to keyboard users: a div with no tabindex is never part of
   * the tab order, and no keydown fires a click(). This patches one up
   * in place, once, without touching whatever click listener the app
   * already attached.
   */
  function makeKeyboardOperable(el, label) {
    if (!el || el.dataset.a11yPatched === 'true') return;
    el.dataset.a11yPatched = 'true';

    if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
    if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
    if (label && !el.hasAttribute('aria-label') && !el.textContent.trim()) {
      el.setAttribute('aria-label', label);
    }

    el.addEventListener('keydown', function (event) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
        event.preventDefault();
        el.click();
      }
    });
  }

  /**
   * Creates an independent focus-trap controller for one drawer/dialog
   * element. `isOpen(el)` decides, from the element's current state,
   * whether the trap should be engaged. `prepare(el)`, if given, runs once
   * per open() call before focus moves, so callers can patch up anything
   * inside the drawer first (e.g. a non-semantic close "button").
   */
  function createFocusTrap(isOpen, prepare, escapeCloser) {
    var activeDrawer = null;
    var triggerElement = null;
    var keydownHandler = null;

    function handleKeydown(event) {
      if (!activeDrawer) return;

      // Only callers that need it pass an escapeCloser -- the theme's own
      // keyup.drawer already covers the native drawer, so it passes none.
      if (event.key === 'Escape' || event.key === 'Esc') {
        if (escapeCloser) {
          event.preventDefault();
          escapeCloser(activeDrawer);
        }
        return;
      }

      if (event.key !== 'Tab') return;

      // Fail open: only block Tab once focus is genuinely inside, or a
      // refused focus() leaves the user with a dead Tab key.
      var focusable = getFocusableElements(activeDrawer);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (!activeDrawer.contains(document.activeElement)) {
        first.focus();
        if (activeDrawer.contains(document.activeElement)) event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    function open(drawer) {
      if (activeDrawer === drawer) return;
      activeDrawer = drawer;
      triggerElement = document.activeElement;

      if (prepare) prepare(drawer);

      if (!drawer.hasAttribute('tabindex')) {
        drawer.setAttribute('tabindex', '-1');
      }
      if (!drawer.hasAttribute('role')) {
        drawer.setAttribute('role', 'dialog');
      }
      if (!drawer.hasAttribute('aria-modal')) {
        drawer.setAttribute('aria-modal', 'true');
      }
      if (!drawer.hasAttribute('aria-label') && !drawer.hasAttribute('aria-labelledby')) {
        drawer.setAttribute('aria-label', 'Filter');
      }

      // Retry until focus takes (~3s): the panel stays visibility:hidden
      // until its open transition finishes, so early attempts find nothing.
      var attemptsLeft = 60;
      (function attemptFocus() {
        if (activeDrawer !== drawer) return;
        var focusable = getFocusableElements(drawer);
        var target = focusable[0] || drawer;
        target.focus();
        if (document.activeElement === target || attemptsLeft <= 0) return;
        attemptsLeft--;
        window.setTimeout(attemptFocus, 50);
      })();

      keydownHandler = handleKeydown;
      document.addEventListener('keydown', keydownHandler, true);
    }

    function close() {
      if (!activeDrawer) return;

      if (keydownHandler) {
        document.removeEventListener('keydown', keydownHandler, true);
        keydownHandler = null;
      }

      if (triggerElement && typeof triggerElement.focus === 'function' && isVisible(triggerElement)) {
        triggerElement.focus();
      }

      activeDrawer = null;
      triggerElement = null;
    }

    return {
      sync: function (drawer) {
        if (isOpen(drawer)) {
          open(drawer);
        } else if (activeDrawer === drawer) {
          close();
        }
      }
    };
  }

  function watch(matches, isOpen, findExisting, prepare, escapeCloser) {
    var trap = createFocusTrap(isOpen, prepare, escapeCloser);

    function check(target) {
      if (target && matches(target)) trap.sync(target);
    }

    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        check(mutations[i].target);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'class'],
      subtree: true
    });

    // In case the drawer is already open when this script runs.
    var existing = findExisting();
    for (var j = 0; j < existing.length; j++) {
      check(existing[j]);
    }
  }

  function init() {
    // Theme-native drawer: open/closed is signalled via aria-hidden.
    watch(
      function (el) {
        return el.id === 't4s-filter-hidden';
      },
      function (el) {
        return el.getAttribute('aria-hidden') === 'false';
      },
      function () {
        var el = document.getElementById('t4s-filter-hidden');
        return el ? [el] : [];
      }
    );

    // Third-party filter app panel: open/closed is signalled by the
    // presence of the "pt-filter-drawer-open" class on the inner
    // .pt-filter-drawer element. isVisible() is checked too, as a safety
    // net in case the app ever changes how it hides the panel — that
    // should never leave focus trapped inside an invisible element.
    watch(
      function (el) {
        return el.classList && el.classList.contains('pt-filter-drawer');
      },
      function (el) {
        return el.classList.contains('pt-filter-drawer-open') && isVisible(el);
      },
      function () {
        return document.querySelectorAll('.pt-filter-drawer');
      },
      function (drawer) {
        // The app's close "button" is a plain <div class="pt-filter-drawer-close-btn">
        // with no role/tabindex, so keyboard users can't reach it at all.
        var closeBtn = drawer.querySelector('.pt-filter-drawer-close-btn');
        makeKeyboardOperable(closeBtn, 'Close filters');
      },
      function (drawer) {
        // Use the app's own close control so its internal state stays in sync.
        var closeBtn = drawer.querySelector('.pt-filter-drawer-close-btn');
        if (closeBtn) closeBtn.click();
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
